import { Box } from "@mui/material";
import { useFormikContext } from "formik";
import { FC, useEffect, useRef } from "react";

import DatepickerFormikMUI from "web/Layout/Datepicker/datepickerFormikMUI";
import {
  BIRTH_DATE_MAX,
  BIRTH_DATE_MIN,
} from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ActiveForm/constants";
import __ from "web/Layout/Translations";

import { parseBirthDateFromPesel } from "web/utils/parseBirthDateFromPesel";
import { peselMask } from "web/utils/peselMask";

import type {
  ISubscriptionReceiverFormValues,
  RequirePeselField,
  RequirePeselValue,
} from "web/types/Subscription";

import CheckboxMUI from "../Checkbox/checkboxMUI";
import FieldMUI from "../Field/fieldMUI";

interface IRequirePeselFormProps {
  field: RequirePeselField;
  tooltipMessage?: string;
  isMobile: boolean;
  disabled?: boolean;
  lang?: {
    key: string;
    code: string;
  };
}

const labels = {
  pl: {
    babyOrForeigner: "Obcokrajowiec / nowonarodzone dziecko",
    pesel: "Pesel",
    birthDate: "Data urodzenia",
  },
  en: {
    babyOrForeigner: "Foreigner/newborn baby",
    pesel: "Pesel number",
    birthDate: "Date of birth",
  },
};

const RequirePeselFormMUI: FC<IRequirePeselFormProps> = ({
  field,
  tooltipMessage,
  isMobile,
  disabled = false,
  lang = { key: "pl_PL", code: "pl" },
}) => {
  const langLabels = labels[lang.code as keyof typeof labels];
  const { values, setFieldValue } =
    useFormikContext<ISubscriptionReceiverFormValues>();

  // Używamy ref do śledzenia poprzedniej wartości PESEL
  const prevPeselRef = useRef<string>("");

  // Automatyczne uzupełnianie daty urodzenia z PESEL
  useEffect(() => {
    const fieldValue = values[field.id] as RequirePeselValue | undefined;
    const peselValue = fieldValue?.pesel || "";
    const babyOrForeigner = fieldValue?.babyOrForeigner;

    // Blokada automatycznego wypełniania gdy zaznaczony jest checkbox "babyOrForeigner"
    if (babyOrForeigner) {
      prevPeselRef.current = peselValue;
      return;
    }

    // Automatycznie wypełnij lub wyczyść gdy PESEL się zmienił
    const peselChanged = peselValue !== prevPeselRef.current;

    if (peselChanged) {
      if (peselValue) {
        const birthDateTimestamp = parseBirthDateFromPesel(peselValue);

        if (birthDateTimestamp !== null) {
          // Ustaw datę urodzenia - zawsze gdy PESEL jest poprawny
          setFieldValue(`${field.id}.birthDate`, birthDateTimestamp);
        }
      } else {
        // Wyczyść datę urodzenia gdy PESEL został usunięty
        setFieldValue(`${field.id}.birthDate`, null);
      }
    }

    // Zapisz aktualny PESEL jako poprzedni
    prevPeselRef.current = peselValue;
  }, [values, field.id, setFieldValue]);

  return (
    <Box sx={{ mt: 2 }}>
      <CheckboxMUI
        name={`${field.id}.babyOrForeigner`}
        dataT1={`${field.id}.babyOrForeigner`}
        tooltipMessage={tooltipMessage}
        disabled={disabled}
      >
        {__(langLabels.babyOrForeigner)}
      </CheckboxMUI>
      <FieldMUI
        name={`${field.id}.pesel`}
        label={__(langLabels.pesel)}
        mask={peselMask}
        placeholder="- - - - - - - - - - -"
        dataT1={`${field.id}.pesel`}
        sx={{ mb: 0, maxWidth: "512px" }}
        disabled={disabled}
      />
      <DatepickerFormikMUI
        name={`${field.id}.birthDate`}
        label={__(langLabels.birthDate)}
        placeholder="DD/MM/YYYY"
        minDate={BIRTH_DATE_MIN}
        maxDate={BIRTH_DATE_MAX}
        isMobile={isMobile}
        sx={{ mt: 2, maxWidth: "512px" }}
        disabled={disabled}
      />
    </Box>
  );
};

export default RequirePeselFormMUI;
